<template>
  <vs-popup class="apply-leave" id="popup" :title="isCreateMode ? 'REQUEST LEAVE' : 'MODIFY LEAVE REQUEST'"
    @close="open = false" :active.sync="open" :buttons-hidden="true">
    <vx-card no-shadow>

      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form id="apply-leave-popup" @submit.prevent="handleSubmit(onClickCreateLeave)" autocomplete="off">

          <div class="mb-3 vx-row" v-if="!isRequestFromProfile && isCreateMode">
            <div class="w-full vx-col md:w-full md:mb-0">
              <ValidationProvider vid="staff_ids" name="staff_ids" rules="required" v-slot="{ errors }">
                <small class="label">Staff Users</small>
                <v-select multiple :close-on-select="false" placeholder="" v-model="form.staff_ids" label="name" :reduce="option => option.value"
                  :options="leaveEnabledStaffs" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                </v-select>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="mb-3 vx-row">
            <div class="w-full vx-col md:w-full md:mb-0">
              <ValidationProvider vid="leave_type" name="leave_type" rules="required" v-slot="{ errors }">
                <small class="label">Leave Type</small>
                <v-select :clearable="false" placeholder="" v-model="form.leave_type" @input="onChangeLeaveType()"
                  :options="leave_types" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                </v-select>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="mb-3 vx-row">
            <div class="w-full vx-col md:w-full md:mb-0">
              <ValidationProvider vid="duration_type" name="duration_type" rules="required" v-slot="{ errors }">
                <small class="label">Select Duration</small>
                <v-select :clearable="false" placeholder="" v-model="form.duration_type" @input="onChangeLeaveType()"
                  :options="duration_types" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                </v-select>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="mb-3 vx-row">
            <div class="w-full vx-col md:w-full md:mb-0">
              <ValidationProvider vid="start_date" name="start_date" rules="required" v-slot="{ errors }">
                <small class="label">Start Date <span class="text-danger" v-if="isRequestFromProfile"> ( Minimum notice: {{ min_notice_days }} days in advance )</span> </small> 
                <flat-pickr :config="flatPkrConfigStartDate" v-model="form.start_date" class="w-full"
                  placeholder="Choose Date" @input="onChangeLeaveType()"/>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="mb-3 vx-row">
            <div class="w-full vx-col md:w-full md:mb-0">
              <ValidationProvider vid="end_date" name="end_date" rules="required" v-slot="{ errors }">
                <small class="label">End Date</small>
                <flat-pickr :disabled="form.duration_type !== 'Full Day'" :config="flatPkrConfigEndDate" v-model="form.end_date" class="w-full"
                  placeholder="Choose Date" />
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <!-- Duration Display -->
          <div class="mb-3 vx-row hidden" v-if="leaveDuration">
            <div class="w-full vx-col">
              <small class="text-primary font-medium">Leave Duration: {{ leaveDuration }}</small>
            </div>
          </div>

          <div class="mb-3 vx-row">
            <div class="w-full vx-col">
              <ValidationProvider vid="reason" name="reason" rules="" v-slot="{ errors }">
                <small class="label">Reason</small>
                <vs-textarea class="mb-0" v-model="form.reason" height="150px" />
                <span class="mt-0 text-xs text-danger">{{ errors[0] }}</span>
                <!-- <span class="mt-0 text-xs text-danger" v-if="form.reason.length < 10 && form.reason.length > 0">Please provide a detailed reason (minimum 10 characters)</span> -->
              </ValidationProvider>
            </div>
          </div>

          <!-- Leave created from Manage Leave is approved on creation; editing there keeps the reviewer optional -->
          <div class="mb-3 vx-row" v-if="require_approval && (isRequestFromProfile || !isCreateMode)">
            <div class="w-full vx-col">
              <ValidationProvider vid="leave_reviewer_id" name="leave_reviewer_id" :rules="isRequestFromProfile ? 'required' : ''" v-slot="{ errors }">
                <small class="label">Request Approval From</small>
                <v-select :clearable="true" placeholder="" v-model="form.leave_reviewer_id" @input="onChangeLeaveType()"
                  :options="leave_reviewers" :dir="$vs.rtl ? 'rtl' : 'ltr'" label="name" :reduce="option => option.id">
                </v-select>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <!-- Save & Reset Button -->
          <div class="flex flex-wrap items-center justify-end mt-8">
            <vs-button button="submit" class="mt-2 ml-auto">Save Changes</vs-button>
            <vs-button @click="form = {}" class="mt-2 ml-4" type="border" color="warning">Reset</vs-button>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </vs-popup>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const LeaveRepository = RepositoryFactory.get('leave');
const DropdownRepository = RepositoryFactory.get('drop_down');

import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import vSelect from 'vue-select'
import moment from 'moment'

import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'

import { mapActions, mapGetters } from 'vuex';

function initialState() {
  return {
    open: false,
    id: '',
    form: {
      leave_type: 'Annual Leave',
      duration_type: 'Full Day',
      reason: '',
      start_date: '',
      end_date: '',
      staff_ids: [],
    },
    min_notice_days: 1, 
    require_approval: true,
    isCreateMode: true,
    leave_types: [
      'Annual Leave', 
      'Sick Leave',
      'Training Leave',
      'Emergency Leave',
      'Unpaid Leave',
      'Maternity Leave',
      'Paternity Leave',
      'Compassionate Leave'
    ],
    duration_types: [
      'Full Day',
      'Morning Half',
      'Afternoon Half'
    ],
    leave_reviewers: []
  }
}

export default {
  name: 'CreateLeavesPopUp',
  data() {
    return initialState()
  },
  props: {
    isRequestFromProfile: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters(['staffs']),
    leaveEnabledStaffs() {
      return this.staffs.filter(staff => staff.is_active && staff.is_leave_enabled)
    },
    flatPkrConfigStartDate() {
      return {
        dateFormat: 'd/m/Y',
        minDate: this.isRequestFromProfile ? 'today' : null,
        locale: {
          firstDayOfWeek: 1
        },
        disable: [
          function (date) {
            return (date.getDay() === 0 || date.getDay() === 6);

          }
        ],
      }
    },
    flatPkrConfigEndDate() {
      return {
        dateFormat: 'd/m/Y',
        minDate: this.form.start_date ? this.form.start_date : 'today',
        locale: {
          firstDayOfWeek: 1
        },
        disable: [
          function (date) {
            return (date.getDay() === 0 || date.getDay() === 6);

          }
        ],
      }
    },
    leaveDuration() {
      if (!this.form.start_date || !this.form.end_date || !this.form.duration_type) {
        return null;
      }

      const startDate = moment(this.form.start_date, 'DD/MM/YYYY');
      const endDate = moment(this.form.end_date, 'DD/MM/YYYY');

      if (!startDate.isValid() || !endDate.isValid()) {
        return null;
      }

      // Calculate business days (excluding weekends)
      let businessDays = 0;
      const current = startDate.clone();
      
      while (current.isSameOrBefore(endDate)) {
        if (current.day() !== 0 && current.day() !== 6) { // 0 = Sunday, 6 = Saturday
          businessDays++;
        }
        current.add(1, 'day');
      }

      // Adjust for half days
      if (this.form.duration_type === 'Morning Half' || this.form.duration_type === 'Afternoon Half') {
        const duration = businessDays * 0.5;
        return duration === 1 ? '0.5 day' : `${duration} days`;
      } else {
        return businessDays === 1 ? '1 day' : `${businessDays} days`;
      }
    },
    current_user_department_ids() {
      return this.$store.getters.currentUser.departments.map(dep => dep.id).toString()
    }
  },
  methods: {
    ...mapActions(['fetchStaffs']),
    async openPopup(id) {
      try {
        await Object.assign(this.$data, initialState());
        this.$refs.form.reset();
        if (!!id) {
          this.id = id
          this.isCreateMode = false
          await this.fetchLeaveDetails()
        } else {
          this.isCreateMode = true
        }
        await this.fetchLeaveSettings()
        await this.fetchLeaveReviewers()
        if(!this.isRequestFromProfile && this.isCreateMode) {
          await this.fetchStaffs()
        }
        this.open = true;
      } catch (error) {

      }
    },
    async fetchLeaveSettings() {
      try {
        const { data } = (await LeaveRepository.getLeaveSettings()).data
        this.min_notice_days = data.min_notice_days.elements[0].value
        this.require_approval = data.require_approval.elements[0].value
      } catch (error) {
        
      }
    },
    async fetchLeaveReviewers() {
      try {
        const { data } = (await DropdownRepository.getStaffLeaveReviewers(0, `filter[department-ids]=${this.current_user_department_ids}`)).data
        this.leave_reviewers = data
      } catch (error) {
        
      }
    },
    onChangeLeaveType() {
      if(this.form.duration_type !== 'Full Day') {
        this.form.end_date = this.form.start_date
      }
    },
    async fetchLeaveDetails() {
      try {
        const { data } = (await LeaveRepository.getLeaveRequestDetails(this.id)).data
        this.form = {
          leave_type: data.leave_type,
          start_date: data.start_date,
          end_date: data.end_date,
          reason: data.reason,
          duration_type: data.duration_type,
          leave_reviewer_id: data.leave_reviewer_id,
          staff_id: data.staff_id
        }
      } catch (error) {
        
      }
    },
    async onClickCreateLeave() {
      const vue = this;
      await this.$vs.loading({
        container: '#apply-leave-popup',
        scale: 0.45
      })
      try {
        if(this.isRequestFromProfile || !this.isCreateMode) {
            delete this.form.staff_id
            delete this.form.staff_ids
        }
        if (this.isCreateMode) {
          await LeaveRepository.requestNewLeave(this.form);
        } else {
          await LeaveRepository.modifyLeaveRequest(this.id, this.form);
        }
        await vue.$parent.refreshData();
        this.open = false;
      } catch (e) {
        this.$vs.loading.close('#apply-leave-popup > .con-vs-loading')
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#apply-leave-popup > .con-vs-loading')
    },
  },
  components: {
    flatPickr,
    'v-select': vSelect,
  },
  mixins: [SuccessMessage, ErrorMessages],
}
</script>

<style scoped>
.apply-leave>>>.vs-popup {
  /*min-height: 50% !important;*/
  min-width: 30% !important;
}
</style>